import { createContext, useContext, useEffect, useState } from "react";

const KEY = "iiitmk_a11y";
// NOTE: only non-sensitive UI accessibility preferences (font size, contrast,
// motion) are persisted here. localStorage is appropriate — no PII/secrets.
const DEFAULTS = {
  fontScale: 1,      // 0.9, 1, 1.15, 1.3, 1.5
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
  readableFont: false,
};

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch (err) {
      // localStorage may be unavailable (private mode) — fall back to defaults.
    }
    return DEFAULTS;
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch (err) {
      // Persisting prefs is best-effort; ignore storage failures.
    }
    const root = document.documentElement;
    root.style.setProperty("--user-font-scale", String(prefs.fontScale));
    root.classList.toggle("a11y-hc", prefs.highContrast);
    root.classList.toggle("a11y-rm", prefs.reduceMotion);
    root.classList.toggle("a11y-underline", prefs.underlineLinks);
    root.classList.toggle("a11y-readable-font", prefs.readableFont);
  }, [prefs]);

  const setFontScale = (fontScale) => setPrefs((p) => ({ ...p, fontScale }));
  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const reset = () => setPrefs(DEFAULTS);

  return (
    <AccessibilityContext.Provider value={{ prefs, setFontScale, toggle, reset }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export const useAccessibility = () => useContext(AccessibilityContext);
